import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Avatar, Chip, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Tooltip, Divider, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Switch, FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Close as CloseIcon, Refresh as RefreshIcon, Person as PersonIcon,
  Phone as PhoneIcon, Email as EmailIcon, MedicalServices as MedicalIcon,
  Schedule as ScheduleIcon, Badge as BadgeIcon, CloudUpload as CloudUploadIcon, DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicPageBreakpoints, clinicPageContainerSx } from '../../hooks/useClinicPageBreakpoints';

const emptyProfessional = {
  full_name: '', email: '', phone: '', cpf: '', registration_number: '',
  registration_state: '', registration_type: 'CRM', specialty: '', bio: '',
  color: '#8b5cf6', commission_percentage: 0, consultation_duration_minutes: 30,
  birth_date: '', hire_date: '', working_hours: {} as Record<string, any>,
  signature_image_url: '',
};

const weekDays = [
  { key: 'monday', label: 'Segunda' }, { key: 'tuesday', label: 'Terça' },
  { key: 'wednesday', label: 'Quarta' }, { key: 'thursday', label: 'Quinta' },
  { key: 'friday', label: 'Sexta' }, { key: 'saturday', label: 'Sábado' },
];

const Professionals: React.FC = () => {
  const { isMobile } = useClinicPageBreakpoints();
  const [professionals, setProfessionals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState(emptyProfessional);
  const [saving, setSaving] = useState(false);
  const [signatureUploading, setSignatureUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const signatureInputRef = useRef<HTMLInputElement>(null);

  const fetchProfessionals = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (search) params.search = search;
      const res = await axios.get(getApiUrl('/clinic/professionals'), { params });
      if (res.data.success) setProfessionals(res.data.data || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchProfessionals(); }, [fetchProfessionals]);

  const handleSave = async () => {
    if (!formData.full_name) { setSnackbar({ open: true, message: 'Nome é obrigatório', severity: 'error' }); return; }
    try {
      setSaving(true);
      if (editingId) {
        await axios.put(getApiUrl(`/clinic/professionals/${editingId}`), formData);
        setSnackbar({ open: true, message: 'Profissional atualizado!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/clinic/professionals'), formData);
        setSnackbar({ open: true, message: 'Profissional cadastrado!', severity: 'success' });
      }
      setDialogOpen(false); fetchProfessionals();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro', severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Desativar este profissional?')) return;
    try {
      await axios.delete(getApiUrl(`/clinic/professionals/${id}`));
      setSnackbar({ open: true, message: 'Profissional desativado!', severity: 'success' });
      fetchProfessionals();
    } catch (err) { setSnackbar({ open: true, message: 'Erro', severity: 'error' }); }
  };

  const handleSignatureFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editingId) return;
    if (file.type !== 'image/png') {
      setSnackbar({ open: true, message: 'Use apenas arquivo PNG.', severity: 'error' });
      return;
    }
    try {
      setSignatureUploading(true);
      const fd = new FormData();
      fd.append('signature', file);
      const res = await axios.post(getApiUrl(`/clinic/professionals/${editingId}/signature-image`), fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.signature_image_url;
      if (url) setFormData((prev) => ({ ...prev, signature_image_url: url }));
      setSnackbar({ open: true, message: 'Assinatura enviada!', severity: 'success' });
      fetchProfessionals();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao enviar assinatura', severity: 'error' });
    } finally {
      setSignatureUploading(false);
    }
  };

  const handleRemoveSignature = async () => {
    if (!editingId) return;
    if (!window.confirm('Remover a imagem da assinatura?')) return;
    try {
      setSignatureUploading(true);
      await axios.delete(getApiUrl(`/clinic/professionals/${editingId}/signature-image`));
      setFormData((prev) => ({ ...prev, signature_image_url: '' }));
      setSnackbar({ open: true, message: 'Assinatura removida.', severity: 'success' });
      fetchProfessionals();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Erro ao remover', severity: 'error' });
    } finally {
      setSignatureUploading(false);
    }
  };

  const updateWorkingHour = (day: string, field: 'start' | 'end', value: string) => {
    const wh = { ...formData.working_hours };
    if (!wh[day]) wh[day] = [{ start: '08:00', end: '18:00' }];
    wh[day][0][field] = value;
    setFormData({ ...formData, working_hours: wh });
  };

  const toggleWorkingDay = (day: string, enabled: boolean) => {
    const wh = { ...formData.working_hours };
    if (enabled) {
      wh[day] = [{ start: '08:00', end: '18:00' }];
    } else {
      delete wh[day];
    }
    setFormData({ ...formData, working_hours: wh });
  };

  return (
    <Box sx={clinicPageContainerSx}>
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        mb: { xs: 2, md: 3 },
        gap: 2,
      }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, color: '#1e1b4b', lineHeight: 1.2 }}>
          Profissionais
        </Typography>
        <Button variant="contained" fullWidth={isMobile} startIcon={<AddIcon />} onClick={() => { setFormData(emptyProfessional); setEditingId(null); setDialogOpen(true); }}
          sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
          Novo Profissional
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: { xs: 2, md: 3 }, alignItems: { xs: 'stretch', sm: 'center' } }}>
        <TextField size="small" fullWidth placeholder="Buscar profissional..." value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9ca3af' }} /></InputAdornment> }}
          sx={{ flex: 1, minWidth: 0, maxWidth: { sm: 420 }, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        <Button variant="outlined" fullWidth={isMobile} startIcon={<RefreshIcon />} onClick={fetchProfessionals} sx={{ textTransform: 'none', flexShrink: 0 }}>
          Atualizar
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#8b5cf6' }} /></Box>
      ) : (
        <Grid container spacing={2}>
          {professionals.map((prof) => (
            <Grid item xs={12} sm={6} md={4} key={prof.id}>
              <Card sx={{ borderRadius: 3, border: '1px solid #e5e7eb', transition: 'all 0.2s',
                '&:hover': { borderColor: prof.color || '#8b5cf6', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' } }}>
                <CardContent>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: prof.color || '#8b5cf6', width: 56, height: 56, fontSize: 22 }}>
                      {prof.full_name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e1b4b', fontSize: 16 }}>
                        {prof.full_name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#8b5cf6', fontWeight: 600 }}>
                        {prof.specialty || 'Especialidade não definida'}
                      </Typography>
                      {prof.registration_number && (
                        <Chip label={`${prof.registration_type} ${prof.registration_number}${prof.registration_state ? `/${prof.registration_state}` : ''}`}
                          size="small" sx={{ mt: 0.5, bgcolor: '#f3e8ff', color: '#7c3aed', fontWeight: 600 }} />
                      )}
                    </Box>
                    <Chip label={prof.is_active ? 'Ativo' : 'Inativo'} size="small"
                      sx={{ bgcolor: prof.is_active ? '#d1fae5' : '#fee2e2', color: prof.is_active ? '#059669' : '#dc2626', fontWeight: 600, alignSelf: 'flex-start' }} />
                  </Box>
                  <Divider sx={{ mb: 1.5 }} />
                  <Grid container spacing={1}>
                    {prof.phone && (
                      <Grid item xs={12}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                        <Typography variant="caption">{prof.phone}</Typography>
                      </Box></Grid>
                    )}
                    {prof.email && (
                      <Grid item xs={12}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                        <Typography variant="caption">{prof.email}</Typography>
                      </Box></Grid>
                    )}
                    <Grid item xs={6}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                      <Typography variant="caption">{prof.consultation_duration_minutes} min</Typography>
                    </Box></Grid>
                    {prof.commission_percentage > 0 && (
                      <Grid item xs={6}><Typography variant="caption" sx={{ color: '#10b981' }}>Comissão: {prof.commission_percentage}%</Typography></Grid>
                    )}
                  </Grid>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, gap: 0.5 }}>
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => { setFormData({ ...emptyProfessional, ...prof }); setEditingId(prof.id); setDialogOpen(true); }}>
                      <EditIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                    <Tooltip title="Desativar"><IconButton size="small" onClick={() => handleDelete(prof.id)}>
                      <DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} /></IconButton></Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {professionals.length === 0 && (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 6, color: '#9ca3af' }}>
                <PersonIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography>Nenhum profissional cadastrado</Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      {/* Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: isMobile
            ? { m: 0, borderRadius: 0, height: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column' }
            : {},
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, px: { xs: 2, sm: 3 } }}>
          {editingId ? 'Editar Profissional' : 'Novo Profissional'}
          <IconButton onClick={() => setDialogOpen(false)} aria-label="Fechar"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Nome Completo *" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Especialidade" value={formData.specialty} onChange={(e) => setFormData({ ...formData, specialty: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small"><InputLabel>Registro</InputLabel>
                <Select value={formData.registration_type} label="Registro" onChange={(e) => setFormData({ ...formData, registration_type: e.target.value })}>
                  <MenuItem value="CRM">CRM</MenuItem><MenuItem value="CRO">CRO</MenuItem>
                  <MenuItem value="CREFITO">CREFITO</MenuItem><MenuItem value="CRP">CRP</MenuItem>
                  <MenuItem value="COREN">COREN</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth label="Nº Registro" value={formData.registration_number} onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small"><InputLabel>UF</InputLabel>
                <Select value={formData.registration_state} label="UF" onChange={(e) => setFormData({ ...formData, registration_state: e.target.value })}>
                  {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map(uf => <MenuItem key={uf} value={uf}>{uf}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}><TextField fullWidth label="CPF" value={formData.cpf} onChange={(e) => setFormData({ ...formData, cpf: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Telefone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Cor na Agenda" type="color" value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Duração Consulta (min)" type="number" value={formData.consultation_duration_minutes} onChange={(e) => setFormData({ ...formData, consultation_duration_minutes: Number(e.target.value) })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Comissão (%)" type="number" value={formData.commission_percentage} onChange={(e) => setFormData({ ...formData, commission_percentage: Number(e.target.value) })} size="small" /></Grid>
            <Grid item xs={12} sm={4}><TextField fullWidth label="Data Admissão" type="date" value={formData.hire_date} onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })} size="small" InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Bio / Descrição" value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} size="small" multiline rows={2} /></Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Assinatura (impressão de documentos)</Typography>
          <Typography variant="caption" display="block" sx={{ color: '#64748b', mb: 1.5 }}>
            PNG, até 1,5&nbsp;MB. É exibida na área do profissional em documentos como recibo, receituário, atestado, encaminhamento, orçamento e pedidos.
          </Typography>
          {!editingId && (
            <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>Salve o cadastro primeiro para poder enviar a imagem da assinatura.</Alert>
          )}
          <Grid container spacing={2} sx={{ mb: 1 }} alignItems="center">
            <Grid item xs={12} sm="auto">
              <Box sx={{ border: '1px dashed #cbd5e1', borderRadius: 2, minHeight: 100, minWidth: { xs: '100%', sm: 280 }, px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc' }}>
                {formData.signature_image_url ? (
                  <Box component="img" alt="Pré-visualização da assinatura" src={formData.signature_image_url}
                    sx={{ maxHeight: 90, maxWidth: 360, objectFit: 'contain' }} />
                ) : (
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>Nenhuma imagem</Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm>
              <input ref={signatureInputRef} type="file" hidden accept="image/png" onChange={handleSignatureFile} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button size="small" variant="outlined"
                  startIcon={signatureUploading ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : <CloudUploadIcon />}
                  disabled={!editingId || signatureUploading} onClick={() => signatureInputRef.current?.click()} sx={{ textTransform: 'none' }}>
                  Enviar PNG
                </Button>
                <Button size="small" color="inherit" variant="outlined" startIcon={<DeleteOutlineIcon />}
                  disabled={!editingId || signatureUploading || !formData.signature_image_url}
                  onClick={handleRemoveSignature} sx={{ textTransform: 'none' }}>
                  Remover
                </Button>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>⏰ Grade de Horários</Typography>
          <Grid container spacing={1}>
            {weekDays.map(({ key, label }) => (
              <Grid item xs={12} key={key}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
                  <FormControlLabel control={
                    <Switch checked={!!formData.working_hours[key]} onChange={(e) => toggleWorkingDay(key, e.target.checked)} size="small" />
                  } label={label} sx={{ width: { xs: '100%', sm: 120 }, m: 0 }} />
                  {formData.working_hours[key] && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, pl: { xs: 0, sm: 0 } }}>
                      <TextField type="time" value={formData.working_hours[key]?.[0]?.start || '08:00'}
                        onChange={(e) => updateWorkingHour(key, 'start', e.target.value)} size="small" sx={{ width: { xs: '100%', sm: 130 } }} />
                      <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>até</Typography>
                      <TextField type="time" value={formData.working_hours[key]?.[0]?.end || '18:00'}
                        onChange={(e) => updateWorkingHour(key, 'end', e.target.value)} size="small" sx={{ width: { xs: '100%', sm: 130 } }} />
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 2 }, flexShrink: 0, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1 }}>
          <Button fullWidth={isMobile} onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button fullWidth={isMobile} variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#14b8a6', '&:hover': { bgcolor: '#0d9488' }, textTransform: 'none', fontWeight: 600 }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Professionals;
